import { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, LinearProgress, MenuItem, Alert, Divider,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import AddIcon from '@mui/icons-material/Add';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/Layout';
import { Page, Section } from '../../components/page/Page';
import { api } from '../../api/api';
import { statusSx, moduleColor } from '../../config/platformModules';

// 신규 점포 온보딩 보드 (12.01-D011, docs/V1_STORE_ONBOARDING_DESIGN.md §7)
const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString()}`);
const WHO = { owner: '점주', digntec: 'Digntec', carrier: '배송' };

function NewStoreDialog({ open, onClose, onCreated }) {
  const empty = { name: '', tljNo: '', ownerName: '', ownerEmail: '', ownerPhone: '', address1: '', town: '', state: '', zipcode: '', screenCount: '', setupFee: 300, monthlyFee: 75 };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/onboarding/stores', f);
      toast.success(`새 점포: ${data.store.name} (${data.store.label})`);
      setF(empty);
      onCreated(data.store);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    setBusy(false);
  };
  const field = (k, label, sm = 6, extra = {}) => (
    <Grid item xs={12} sm={sm}><TextField fullWidth size="small" label={label} value={f[k]} onChange={set(k)} {...extra} /></Grid>
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>새 점포 (온보딩 시작)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          정보가 다 차면 바로 '정보 입력 완료' 단계로 시작합니다. 비어 있으면 점주 정보 입력 단계부터.
        </Typography>
        <Grid container spacing={1.5}>
          {field('name', '매장명 *', 8)}{field('tljNo', 'TLJ 번호', 4)}
          {field('ownerName', '점주 이름')}{field('ownerEmail', '점주 이메일')}
          {field('ownerPhone', '점주 전화')}{field('screenCount', '화면 수', 6, { type: 'number' })}
          {field('address1', '주소', 12)}
          {field('town', '도시', 5)}{field('state', '주', 3)}{field('zipcode', 'ZIP', 4)}
          {field('setupFee', '설치비 ($)', 6, { type: 'number' })}{field('monthlyFee', '월 요금 ($, ACH)', 6, { type: 'number' })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={save} disabled={busy || !f.name.trim()}>만들기</Button>
      </DialogActions>
    </Dialog>
  );
}

function StoreDialog({ storeId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [skipTo, setSkipTo] = useState('');
  const [reason, setReason] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!storeId) return;
    try { const { data } = await api.get(`/onboarding/stores/${storeId}`); setD(data); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  }, [storeId]);
  useEffect(() => { setD(null); setSkipTo(''); setReason(''); setInviteEmail(''); load(); }, [load]);
  if (!storeId) return null;
  const s = d?.summary;
  const act = async (path, body, ok) => {
    setBusy(true);
    try { await api.post(`/onboarding/stores/${storeId}/${path}`, body); toast.success(ok); setReason(''); setSkipTo(''); await load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    setBusy(false);
  };
  const nextStage = s && d.stages[s.index + 1]?.stage;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{s ? `${s.name} — ${s.label}` : '불러오는 중…'}</DialogTitle>
      <DialogContent dividers>
        {s && (
          <>
            <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
              {d.stages.map((st, i) => (
                <Chip key={st.stage} size="small" label={`${i + 1}. ${st.label}`}
                  color={i < s.index ? 'success' : i === s.index ? 'primary' : 'default'}
                  variant={i === s.index ? 'filled' : 'outlined'} sx={{ mb: 0.5 }} />
              ))}
            </Stack>
            {s.next && <Alert severity="info" sx={{ mb: 2 }}>다음: [{WHO[s.next.who] || '-'}] {s.next.text}</Alert>}
            {s.blockers?.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>입력 필요: {s.blockers.join(', ')}</Alert>}
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">설치비</Typography><Typography>{money(s.setupPayment?.amount ?? s.terms?.setupFee)} · {s.setupPayment?.status || '청구 전'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">계약</Typography><Typography>{s.contractStatus || 'none'}</Typography></Grid>
              <Grid item xs={12} sm={4}><Typography variant="caption" color="text.secondary">배송</Typography><Typography>{s.shippingStatus || 'pending'}{s.shippingTrackingNumber ? ` · ${s.shippingTrackingNumber}` : ''}</Typography></Grid>
            </Grid>
            {s.stage === 'installing' && d.checklist?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>오픈 체크리스트</Typography>
                {d.checklist.map((c) => <Typography key={c.key} variant="body2">{c.ok ? '✅' : '⬜'} {c.label}</Typography>)}
              </Box>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
              {nextStage && nextStage !== 'open' && (
                <Button variant="contained" disabled={busy} onClick={() => act('advance', { to: nextStage }, '다음 단계로 이동')}>
                  다음 단계: {d.stages[s.index + 1].label}
                </Button>
              )}
              {s.stage !== 'open' && (
                <Button variant="contained" color="success" disabled={busy} onClick={() => act('open', { reason }, '오픈 처리 — 운영 중')}>오픈 처리</Button>
              )}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
              <TextField select size="small" label="급한 진행 / 되돌리기" value={skipTo} onChange={(e) => setSkipTo(e.target.value)} sx={{ minWidth: 220 }}>
                {d.stages.filter((st) => st.stage !== s.stage && st.stage !== 'open').map((st) => <MenuItem key={st.stage} value={st.stage}>{st.label}</MenuItem>)}
              </TextField>
              <TextField size="small" label="사유 (필수: 건너뛰기·되돌리기·체크리스트 미완료 오픈)" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ flex: 1, minWidth: 240 }} />
              <Button variant="outlined" disabled={busy || !skipTo || reason.trim().length < 3} onClick={() => act('advance', { to: skipTo, reason }, '사유와 함께 이동')}>이동</Button>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontWeight: 700, mb: 1 }}>점주 초대 (가입 방법 B)</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField size="small" label="점주 이메일" value={inviteEmail || s.ownerEmail || ''} onChange={(e) => setInviteEmail(e.target.value)} sx={{ flex: 1 }} />
              <Button variant="outlined" disabled={busy} onClick={async () => {
                setBusy(true);
                try {
                  const { data } = await api.post(`/onboarding/stores/${storeId}/invite`, { email: inviteEmail || s.ownerEmail, as: 'owner' });
                  toast.success(`초대 메일 발송: ${data.email}`);
                  await load(); onChanged();
                } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
                setBusy(false);
              }}>초대 메일 보내기</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">점주가 메일의 링크에서 계정·매장 정보를 넣으면 자동으로 이 매장에 연결됩니다 (14일, 1회용).</Typography>
            <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>타임라인</Typography>
            {d.events.map((e) => (
              <Box key={e._id || e.createdAt} sx={{ display: 'flex', gap: 1.5, py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="body2" sx={{ width: 120, flexShrink: 0, color: 'text.secondary' }}>{fmt(e.createdAt)}</Typography>
                <Typography variant="body2">
                  {(d.stages.find((x) => x.stage === e.to) || {}).label || e.to}
                  {e.action === 'auto' && <Chip size="small" label="자동" sx={{ ml: 1, height: 18 }} />}
                  {e.skipped && <Chip size="small" color="warning" label="건너뜀" sx={{ ml: 1, height: 18 }} />}
                  {e.reason && <Typography component="span" variant="body2" color="text.secondary"> — {e.reason}</Typography>}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>닫기</Button></DialogActions>
    </Dialog>
  );
}

function OnboardingBoard({ user }) {
  const [data, setData] = useState({ stages: [], stores: [] });
  const [filter, setFilter] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    try { const { data: r } = await api.get('/onboarding/board'); setData(r); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const rows = filter ? data.stores.filter((s) => s.stage === filter) : data.stores;
  return (
    <Layout title="Onboarding" user={user}>
      <Page
        icon={<FlagIcon sx={{ color: moduleColor('core') }} />}
        title="신규 점포 온보딩"
        subtitle="계약서(대표 서명) → 서명 → 설치비 → ACH → 배송 → 설치 → 오픈. 급한 경우 사유를 적고 건너뛸 수 있습니다."
        actions={user?.role === 'admin' && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)} sx={{ bgcolor: '#551FFF' }}>새 점포</Button>}
      >
        <Section>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Chip label={`전체 ${data.stores.length}`} color={!filter ? 'primary' : 'default'} onClick={() => setFilter('')} sx={{ mb: 1 }} />
            {data.stages.filter((s) => s.stage !== 'open').map((s) => (
              <Chip key={s.stage} label={`${s.label} ${s.count}`} color={filter === s.stage ? 'primary' : 'default'}
                variant={s.count ? 'filled' : 'outlined'} onClick={() => setFilter(s.stage)} sx={{ mb: 1 }} />
            ))}
          </Stack>
        </Section>
        <Section>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 820, '& th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>매장</TableCell><TableCell>단계</TableCell><TableCell>경과</TableCell>
                  <TableCell>다음 할 일</TableCell><TableCell>설치비</TableCell><TableCell>계약 / 배송</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>온보딩 중인 점포가 없습니다</TableCell></TableRow>
                )}
                {rows.map((s) => (
                  <TableRow key={s._id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenId(s._id)}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{[s.tljNo, s.town, s.state].filter(Boolean).join(' · ')}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Typography variant="body2">{s.label}</Typography>
                      <LinearProgress variant="determinate" value={((s.index + 1) / s.total) * 100} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={`${s.daysInStage ?? 0}일`} sx={statusSx(s.daysInStage >= 14 ? 'critical' : s.daysInStage >= 7 ? 'warn' : 'ok')} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap>[{WHO[s.next?.who] || '-'}] {s.next?.text}</Typography>
                      {s.blockers?.length > 0 && <Typography variant="caption" sx={{ color: '#CA8A04' }}>입력 필요: {s.blockers.join(', ')}</Typography>}
                    </TableCell>
                    <TableCell>{money(s.setupPayment?.amount ?? s.terms?.setupFee)}<br />
                      <Chip size="small" label={s.setupPayment?.status || '청구 전'} sx={{ mt: 0.5, height: 20, ...statusSx(s.setupPayment?.status === 'paid' ? 'ok' : s.setupPayment?.status === 'overdue' ? 'critical' : 'warn') }} />
                    </TableCell>
                    <TableCell><Typography variant="body2">{s.contractStatus || 'none'}</Typography><Typography variant="caption" color="text.secondary">{s.shippingStatus || 'pending'}</Typography></TableCell>
                    <TableCell align="right"><Button size="small">열기</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      </Page>
      <NewStoreDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={(st) => { setNewOpen(false); load(); setOpenId(st._id); }} />
      <StoreDialog storeId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </Layout>
  );
}

export default OnboardingBoard;
